import type { Order, OrderItem } from '$models/Order/Order';
import { getContext, setContext } from 'svelte';
import { LocalStorage } from './local-storage.svelte';

const key = Symbol('orderDraftState');

export type OrderDraft = {
	id: string | null;
	frequency: number;
	products: OrderItem[];
	nonSubscribableProducts: OrderItem[];
	couponCode: string | null;
	billingAddressId: string | null;
	shippingAddressId: string | null;
	deliveryNote: string | null;
	poNumber: string | null;
	deliveryDate: string | null;
	deliveryCompanyName: string | null;
};

type DraftInput = Omit<Partial<OrderDraft>, 'deliveryDate'> & {
	deliveryDate?: string | Date | null;
};

type DraftUpdater =
	| Partial<OrderDraft>
	| ((draft: OrderDraft | null) => Partial<OrderDraft> | OrderDraft | null);

type OrderDraftState = ReturnType<typeof createOrderDraftState>;

function normalizeDraft(orderLike: DraftInput | null | undefined): OrderDraft | null {
	if (!orderLike) {
		return null;
	}

	const deliveryDate = orderLike.deliveryDate ? new Date(orderLike.deliveryDate) : null;

	return {
		id: orderLike.id ?? null,
		frequency: orderLike.frequency ?? 0,
		products: orderLike.products ?? [],
		nonSubscribableProducts: orderLike.nonSubscribableProducts ?? [],
		couponCode: orderLike.couponCode ?? null,
		billingAddressId: orderLike.billingAddressId ?? null,
		shippingAddressId: orderLike.shippingAddressId ?? null,
		deliveryNote: orderLike.deliveryNote ?? null,
		poNumber: orderLike.poNumber ?? null,
		deliveryDate:
			deliveryDate && !Number.isNaN(deliveryDate.getTime())
				? typeof orderLike.deliveryDate === 'string'
					? orderLike.deliveryDate
					: deliveryDate.toISOString()
				: null,
		deliveryCompanyName: orderLike.deliveryCompanyName ?? null
	};
}

export function createOrderDraftState() {
	const storedDraft = new LocalStorage<OrderDraft | null>('cart', null);
	let draft = $derived(normalizeDraft(storedDraft.current));

	function setDraft(orderLike: DraftInput | null | undefined) {
		storedDraft.current = normalizeDraft(orderLike);
	}

	function updateDraft(patchOrUpdater: DraftUpdater) {
		const current = normalizeDraft(storedDraft.current);
		const patch = typeof patchOrUpdater === 'function' ? patchOrUpdater(current) : patchOrUpdater;

		if (patch === null) {
			setDraft(null);
			return;
		}

		storedDraft.current = normalizeDraft({
			...(current ?? {}),
			...(patch ?? {})
		});
	}

	function attachSyncedOrder(order: Partial<Order> | DraftInput) {
		updateDraft({
			id: order.id ?? null,
			billingAddressId: order.billingAddressId ?? draft?.billingAddressId ?? null,
			shippingAddressId: order.shippingAddressId ?? draft?.shippingAddressId ?? null,
			deliveryDate:
				order.deliveryDate instanceof Date
					? order.deliveryDate.toISOString()
					: (order.deliveryDate ?? null),
			deliveryCompanyName: order.deliveryCompanyName ?? null
		});
	}

	function clear() {
		storedDraft.current = null;
	}

	function anonymize() {
		if (!draft) {
			clear();
			return;
		}

		storedDraft.current = {
			...draft,
			id: null,
			billingAddressId: null,
			shippingAddressId: null,
			deliveryNote: null,
			poNumber: null,
			deliveryDate: null,
			deliveryCompanyName: null
		};
	}

	function toOrderPayload() {
		if (!draft) {
			return null;
		}

		return {
			billingAddressId: draft.billingAddressId,
			shippingAddressId: draft.shippingAddressId,
			frequency: draft.frequency,
			products: draft.products,
			nonSubscribableProducts: draft.nonSubscribableProducts,
			deliveryNote: draft.deliveryNote,
			poNumber: draft.poNumber
		};
	}

	return {
		get current() {
			return draft;
		},
		setDraft,
		updateDraft,
		attachSyncedOrder,
		clear,
		anonymize,
		toOrderPayload
	};
}

export function setOrderDraftState(orderDraftState: OrderDraftState) {
	setContext(key, orderDraftState);
}

export function getOrderDraftState(): OrderDraftState {
	return getContext(key);
}
